import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { RootState, AppDispatch } from '@/store/store'
import {
    FiSettings,
    FiBell,
    FiLock,
    FiUser,
    FiMail,
    FiPhone,
    FiEye,
    FiEyeOff,
    FiTrash2,
    FiDownload,
    FiSun,
    FiMoon,
    FiDollarSign,
    FiRefreshCw
} from 'react-icons/fi'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import DeleteAccountDialog from '@/components/ui/DeleteAccountDialog'
import SelectDropdown, { SelectOption } from '@/components/ui/SelectDropdown'
import { userApi } from '@/services/api/users'
import { updateUser, logoutUser } from '@/store/slices/authSlice'
import { useTheme } from '@/hooks/useTheme'
import { useCacheInvalidation } from '@/hooks/useCacheInvalidation'
import { CurrencySettings } from '@/components/currency'
import { downloadJson, downloadPDF, generateTimestampedFilename } from '@/utils/downloadUtils'

interface NotificationSettings {
    emailNotifications: boolean
    pushNotifications: boolean
    smsNotifications: boolean
    gigAlerts: boolean
    applicationUpdates: boolean
    messageNotifications: boolean
    marketingEmails: boolean
}

interface PrivacySettings {
    profileVisibility: 'public' | 'private' | 'registered'
    showLocation: boolean
    showContactInfo: boolean
    showLastSeen: boolean
}

interface AccountSettings {
    twoFactorEnabled: boolean
    loginAlerts: boolean
    sessionTimeout: number
}

interface AccessibleSwitchProps {
    checked: boolean
    label: string
    onClick: () => void
    className: string
    children?: React.ReactNode
}

const AccessibleSwitch: React.FC<AccessibleSwitchProps> = ({ checked, label, onClick, className, children }) => {
    if (checked) {
        return (
            <button
                onClick={onClick}
                aria-label={`Disable ${label}`}
                aria-checked="true"
                role="switch"
                className={className}
            >
                {children}
            </button>
        )
    }

    return (
        <button
            onClick={onClick}
            aria-label={`Enable ${label}`}
            aria-checked="false"
            role="switch"
            className={className}
        >
            {children}
        </button>
    )
}

const SettingsPage: React.FC = () => {
    const { user } = useSelector((state: RootState) => state.auth)
    const { showSuccess, showError } = useSnackbarContext()
    const { isDarkMode, toggleTheme } = useTheme()
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams()
    const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'privacy' | 'account' | 'currency' | 'data'>('profile')

    // Profile form state
    const [profileForm, setProfileForm] = useState({
        firstName: user?.profile?.firstName || '',
        lastName: user?.profile?.lastName || '',
        phone: user?.profile?.phone || '',
        bio: user?.profile?.bio || ''
    })

    // Update form when user data changes
    useEffect(() => {
        if (user?.profile) {
            setProfileForm({
                firstName: user.profile.firstName || '',
                lastName: user.profile.lastName || '',
                phone: user.profile.phone || '',
                bio: user.profile.bio || ''
            })
        }

        // Initialize settings from user data if available
        if (user) {
            // Initialize notification settings
            if (user.communicationPreferences) {
                setNotificationSettings({
                    emailNotifications: user.communicationPreferences.emailNotifications ?? true,
                    pushNotifications: user.communicationPreferences.pushNotifications ?? true,
                    smsNotifications: user.communicationPreferences.smsNotifications ?? false,
                    gigAlerts: user.preferences?.jobAlerts ?? true,
                    applicationUpdates: true, // Default value since not in user model
                    messageNotifications: true, // Default value
                    marketingEmails: user.communicationPreferences.marketingEmails ?? false
                })
            }

            // Initialize account security settings
            setAccountSettings({
                twoFactorEnabled: user.twoFactorEnabled ?? false,
                loginAlerts: user.communicationPreferences?.securityAlerts ?? true,
                sessionTimeout: (user.preferences as any)?.sessionTimeout ?? 30
            })

            // Initialize privacy settings from user preferences
            const userPrivacy = (user.preferences as any)?.privacy
            if (userPrivacy) {
                setPrivacySettings({
                    profileVisibility: userPrivacy.profileVisibility || 'public',
                    showLocation: userPrivacy.showLocation !== undefined ? userPrivacy.showLocation : true,
                    showContactInfo: userPrivacy.showContactInfo !== undefined ? userPrivacy.showContactInfo : false,
                    showLastSeen: userPrivacy.showLastSeen !== undefined ? userPrivacy.showLastSeen : true
                })
            }
        }
    }, [user])

    // Handle URL tab parameter
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab') as 'profile' | 'notifications' | 'privacy' | 'account' | 'currency' | 'data'
        if (tabFromUrl && ['profile', 'notifications', 'privacy', 'account', 'currency', 'data'].includes(tabFromUrl)) {
            setActiveTab(tabFromUrl)
        }
    }, [searchParams])

    const handleTabChange = (tab: 'profile' | 'notifications' | 'privacy' | 'account' | 'currency' | 'data') => {
        setActiveTab(tab)
        setSearchParams({ tab })
    }

    // Settings states
    const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
        emailNotifications: true,
        pushNotifications: true,
        smsNotifications: false,
        gigAlerts: true,
        applicationUpdates: true,
        messageNotifications: true,
        marketingEmails: false
    })

    const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
        profileVisibility: 'public',
        showLocation: true,
        showContactInfo: false,
        showLastSeen: true
    })

    const [accountSettings, setAccountSettings] = useState<AccountSettings>({
        twoFactorEnabled: false,
        loginAlerts: true,
        sessionTimeout: 30
    })

    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    })

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    })

    const {
        invalidateSubscriptionCache,
        invalidateAccessLimitsCache,
        invalidateVerificationCache,
        invalidateStatsCache,
        invalidateProfileViewCache,
        invalidateAllCaches,
        resetAllCaches,
    } = useCacheInvalidation()

    const [cacheAction, setCacheAction] = useState<'subscription' | 'access' | 'verification' | 'stats' | 'profile' | 'all' | 'reset' | null>(null)
    const cacheActionInProgress = cacheAction !== null

    const handleCacheAction = (action: 'subscription' | 'access' | 'verification' | 'stats' | 'profile' | 'all' | 'reset') => {
        setCacheAction(action)
        try {
            switch (action) {
                case 'subscription':
                    invalidateSubscriptionCache()
                    showSuccess('Subscription cache cleared')
                    break
                case 'access':
                    invalidateAccessLimitsCache()
                    showSuccess('Access limits cache cleared')
                    break
                case 'verification':
                    invalidateVerificationCache()
                    showSuccess('Verification cache cleared')
                    break
                case 'stats':
                    invalidateStatsCache()
                    showSuccess('Analytics cache cleared')
                    break
                case 'profile':
                    invalidateProfileViewCache()
                    showSuccess('Profile views cache cleared')
                    break
                case 'all':
                    invalidateAllCaches()
                    showSuccess('All caches invalidated')
                    break
                case 'reset':
                    resetAllCaches()
                    showSuccess('Cache state reset')
                    break
            }
        } catch (error: any) {
            console.error('Cache management error:', error)
            showError(error?.message || 'Failed to manage cache. Please try again.')
        } finally {
            setCacheAction(null)
        }
    }

    // Dialog states
    const [showDeactivateDialog, setShowDeactivateDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)

    const handleNotificationChange = async (key: keyof NotificationSettings, value: boolean) => {
        setNotificationSettings(prev => ({
            ...prev,
            [key]: value
        }))

        try {
            const updateData = { [key]: value }
            const apiResponse = await userApi.updateNotificationPreferences(updateData)

            if (apiResponse.success) {
                showSuccess('Notification settings updated')
            } else {
                throw new Error('Failed to update notification settings')
            }
        } catch (error: any) {
            console.error('Notification update error:', error)
            showError(error.message || 'Failed to update notification settings')
            // Revert the state change on error
            setNotificationSettings(prev => ({
                ...prev,
                [key]: !value
            }))
        }
    }

    const handlePrivacyChange = async (key: keyof PrivacySettings, value: any) => {
        const oldValue = privacySettings[key]
        setPrivacySettings(prev => ({
            ...prev,
            [key]: value
        }))

        try {
            const updateData = { [key]: value }
            const apiResponse = await userApi.updatePrivacySettings(updateData)

            if (apiResponse.success) {
                showSuccess('Privacy settings updated')
            } else {
                throw new Error('Failed to update privacy settings')
            }
        } catch (error: any) {
            console.error('Privacy update error:', error)
            showError(error.message || 'Failed to update privacy settings')
            // Revert the state change on error
            setPrivacySettings(prev => ({
                ...prev,
                [key]: oldValue
            }))
        }
    }

    const handleAccountChange = async (key: keyof AccountSettings, value: any) => {
        const oldValue = accountSettings[key]
        setAccountSettings(prev => ({
            ...prev,
            [key]: value
        }))

        try {
            const updateData = { [key]: value }
            const apiResponse = await userApi.updateAccountSettings(updateData)

            if (apiResponse.success) {
                showSuccess('Account settings updated')
            } else {
                throw new Error('Failed to update account settings')
            }
        } catch (error: any) {
            console.error('Account settings update error:', error)
            showError(error.message || 'Failed to update account settings')
            // Revert the state change on error
            setAccountSettings(prev => ({
                ...prev,
                [key]: oldValue
            }))
        }
    }

    const handleProfileFormChange = (field: keyof typeof profileForm, value: string) => {
        setProfileForm(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const handleProfileSave = async (e?: React.FormEvent) => {
        if (e) e.preventDefault()

        setLoading(true)
        try {
            const apiResponse = await userApi.updateProfile(profileForm)

            if (apiResponse.success && apiResponse.data?.user) {
                // Update the user in the Redux store
                dispatch(updateUser(apiResponse.data.user))
                showSuccess('Profile updated successfully')
            } else {
                throw new Error('Failed to update profile')
            }
        } catch (error: any) {
            console.error('Profile update error:', error)
            showError(error.message || 'Failed to update profile. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handlePasswordUpdate = async (e: React.FormEvent) => {
        e.preventDefault()

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            showError('New passwords do not match')
            return
        }

        if (passwordForm.newPassword.length < 6) {
            showError('New password must be at least 6 characters')
            return
        }

        setLoading(true)
        try {
            const apiResponse = await userApi.updatePassword({
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword
            })

            if (apiResponse.success) {
                showSuccess('Password updated successfully. Please log in with your new password.')
                setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                })

                // Log out the user and redirect to login page
                setTimeout(async () => {
                    await dispatch(logoutUser())
                    navigate('/login')
                }, 2000) // Give time for success message to be seen
            } else {
                throw new Error(apiResponse.message || 'Failed to update password')
            }
        } catch (error: any) {
            console.error('Password update error:', error)
            showError(error.message || 'Failed to update password')
        } finally {
            setLoading(false)
        }
    }

    const handleDataExport = async () => {
        setLoading(true)
        try {
            // Try PDF download first (user-friendly format)
            const pdfBlob = await userApi.getUserDataExportPDF()

            // Generate filename with timestamp
            const filename = generateTimestampedFilename('sidego-user-data', 'pdf')

            // Download the PDF
            downloadPDF(pdfBlob, filename)

            showSuccess('Your data has been downloaded as PDF successfully!')
        } catch (error: any) {
            console.error('PDF export error:', error)

            // Fallback to JSON download if PDF fails
            try {
                const apiResponse = await userApi.getUserDataExport()

                if (apiResponse.success) {
                    const { userData, exportedAt, version } = apiResponse.data

                    // Create comprehensive export data
                    const exportData = {
                        exportInfo: {
                            exportedAt,
                            version,
                            exportedBy: user?.profile?.firstName + ' ' + user?.profile?.lastName,
                            userId: user?._id
                        },
                        ...userData
                    }

                    // Generate filename with timestamp
                    const filename = generateTimestampedFilename('sidego-user-data', 'json')

                    // Download the data as JSON
                    downloadJson(exportData, filename.replace('.json', ''))

                    showSuccess('PDF unavailable. Your data has been downloaded as JSON instead!')
                } else {
                    throw new Error('Failed to export data')
                }
            } catch (jsonError: any) {
                console.error('JSON export error:', jsonError)

                // Final fallback to email-based export
                try {
                    const fallbackApiResponse = await userApi.exportUserData()

                    if (fallbackApiResponse.success) {
                        showSuccess('Direct download unavailable. Data export initiated - you will receive an email with your data shortly.')
                    } else {
                        throw new Error('All download methods failed')
                    }
                } catch (fallbackError: any) {
                    console.error('Fallback export error:', fallbackError)
                    showError('Unable to export data at this time. Please try again later.')
                }
            }
        } finally {
            setLoading(false)
        }
    }

    const handleAccountDeactivation = async () => {
        setLoading(true)
        try {
            const apiResponse = await userApi.deactivateAccount()

            if (apiResponse.success) {
                showSuccess('Account deactivation initiated. Your account will be suspended for 30 days.')
                setShowDeactivateDialog(false)
                // Optionally redirect or logout
                setTimeout(() => {
                    window.location.href = '/login'
                }, 2000)
            } else {
                throw new Error('Failed to deactivate account')
            }
        } catch (error: any) {
            console.error('Account deactivation error:', error)
            showError(error.message || 'Failed to deactivate account')
        } finally {
            setLoading(false)
        }
    }

    const handleAccountDeletion = async () => {
        setLoading(true)
        try {
            const apiResponse = await userApi.deleteAccount()

            if (apiResponse.success) {
                showSuccess('Account has been permanently deleted.')
                setShowDeleteDialog(false)
                // Redirect to home page after deletion
                setTimeout(() => {
                    window.location.href = '/'
                }, 2000)
            } else {
                throw new Error('Failed to delete account')
            }
        } catch (error: any) {
            console.error('Account deletion error:', error)
            showError(error.message || 'Failed to delete account')
        } finally {
            setLoading(false)
        }
    }

    const tabs = [
        { id: 'profile', label: 'Profile', icon: <FiUser className="w-4 h-4 sm:w-5 sm:h-5" /> },
        { id: 'notifications', label: 'Notifications', icon: <FiBell className="w-4 h-4 sm:w-5 sm:h-5" /> },
        { id: 'privacy', label: 'Privacy', icon: <FiLock className="w-4 h-4 sm:w-5 sm:h-5" /> },
        { id: 'account', label: 'Account Security', icon: <FiSettings className="w-4 h-4 sm:w-5 sm:h-5" /> },
        { id: 'currency', label: 'Currency', icon: <FiDollarSign className="w-4 h-4 sm:w-5 sm:h-5" /> },
        { id: 'data', label: 'Data & Privacy', icon: <FiDownload className="w-4 h-4 sm:w-5 sm:h-5" /> }
    ]

    const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }))
    }

    return (
        <>
            <Helmet>
                <title>Settings - SideGO</title>
                <meta name="description" content="Manage your account settings and preferences" />
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
                {/* Page Header */}
                <div className="mb-6 sm:mb-8">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                        <FiSettings className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 flex-shrink-0" />
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
                            Settings
                        </h1>
                    </div>
                    <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                        Manage your account settings, notifications, and privacy preferences
                    </p>
                </div>

                {/* Horizontal Tabs */}
                <div className="mb-6 sm:mb-8">
                    <div className="border-b border-gray-200 dark:border-gray-700">
                        <nav className="-mb-px flex space-x-2 sm:space-x-4 md:space-x-6 overflow-x-auto scrollbar-hide">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabChange(tab.id as any)}
                                    className={`whitespace-nowrap py-3 sm:py-4 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm md:text-base transition-colors flex items-center justify-center gap-1 sm:gap-2 ${activeTab === tab.id
                                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    <span className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 flex items-center justify-center">{tab.icon}</span>
                                    <span className="leading-tight">{tab.label}</span>
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                {/* Content Area */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="p-4 sm:p-5 md:p-6">
                        {/* Profile Settings */}
                        {activeTab === 'profile' && (
                            <div>
                                <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
                                    Profile Information
                                </h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                                    <div>
                                        <label htmlFor="settings-first-name" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            First Name
                                        </label>
                                        <input
                                            id="settings-first-name"
                                            type="text"
                                            value={profileForm.firstName}
                                            onChange={(e) => handleProfileFormChange('firstName', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-xs sm:text-sm md:text-base text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="settings-last-name" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Last Name
                                        </label>
                                        <input
                                            id="settings-last-name"
                                            type="text"
                                            value={profileForm.lastName}
                                            onChange={(e) => handleProfileFormChange('lastName', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-xs sm:text-sm md:text-base text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="settings-email" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            <FiMail className="w-4 h-4 inline mr-2" />
                                            Email Address
                                        </label>
                                        <input
                                            id="settings-email"
                                            type="email"
                                            value={user?.email || ''}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-600 text-xs sm:text-sm md:text-base text-gray-500 dark:text-gray-400"
                                            disabled
                                        />
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            Contact support to change your email address
                                        </p>
                                    </div>

                                    <div>
                                        <label htmlFor="settings-phone" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            <FiPhone className="w-4 h-4 inline mr-2" />
                                            Phone Number
                                        </label>
                                        <input
                                            id="settings-phone"
                                            type="tel"
                                            value={profileForm.phone}
                                            onChange={(e) => handleProfileFormChange('phone', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-xs sm:text-sm md:text-base text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <label htmlFor="settings-bio" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Bio
                                    </label>
                                    <textarea
                                        id="settings-bio"
                                        rows={4}
                                        value={profileForm.bio}
                                        onChange={(e) => handleProfileFormChange('bio', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-xs sm:text-sm md:text-base text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Tell us about yourself..."
                                    />
                                </div>

                                <div className="mt-6">
                                    <button
                                        onClick={handleProfileSave}
                                        disabled={loading}
                                        className={`px-6 py-2 rounded-lg transition-colors flex items-center gap-2 ${loading
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-blue-600 hover:bg-blue-700'
                                            } text-white`}
                                    >
                                        {loading && <LoadingSpinner size="sm" />}
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Notification Settings */}
                        {activeTab === 'notifications' && (
                            <div>
                                <h2 className="text-lg sm:text-xl md:text-xl font-semibold text-gray-900 dark:text-white mb-6">
                                    Notification Preferences
                                </h2>

                                <div className="space-y-6">
                                    <div className="border-b border-gray-200 dark:border-gray-600 pb-4">
                                        <h3 className="text-base sm:text-lg md:text-lg font-medium text-gray-900 dark:text-white mb-4">
                                            Delivery Methods
                                        </h3>
                                        <div className="space-y-3">
                                            {[
                                                { key: 'emailNotifications', label: 'Email Notifications', icon: <FiMail className="w-4 h-4" /> },
                                                { key: 'pushNotifications', label: 'Push Notifications', icon: <FiBell className="w-4 h-4" /> },
                                                { key: 'smsNotifications', label: 'SMS Notifications', icon: <FiPhone className="w-4 h-4" /> }
                                            ].map(item => (
                                                <div key={item.key} className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        {item.icon}
                                                        <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
                                                    </div>
                                                    <AccessibleSwitch
                                                        checked={notificationSettings[item.key as keyof NotificationSettings]}
                                                        label={item.label}
                                                        onClick={() => handleNotificationChange(item.key as keyof NotificationSettings, !notificationSettings[item.key as keyof NotificationSettings])}
                                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings[item.key as keyof NotificationSettings]
                                                            ? 'bg-blue-600'
                                                            : 'bg-gray-200 dark:bg-gray-600'
                                                            }`}
                                                    >
                                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${notificationSettings[item.key as keyof NotificationSettings] ? 'translate-x-6' : 'translate-x-1'
                                                            }`} />
                                                    </AccessibleSwitch>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-base sm:text-lg md:text-lg font-medium text-gray-900 dark:text-white mb-4">
                                            Notification Types
                                        </h3>
                                        <div className="space-y-3">
                                            {[
                                                { key: 'gigAlerts', label: 'New Gig Alerts', description: 'Get notified when gigs matching your skills are posted' },
                                                { key: 'applicationUpdates', label: 'Application Updates', description: 'Status changes on your applications' },
                                                { key: 'messageNotifications', label: 'Message Notifications', description: 'New messages and chat updates' },
                                                { key: 'marketingEmails', label: 'Marketing Emails', description: 'Product updates, tips, and promotional content' }
                                            ].map(item => (
                                                <div key={item.key} className="flex items-start justify-between">
                                                    <div>
                                                        <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 font-medium">{item.label}</span>
                                                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{item.description}</p>
                                                    </div>
                                                    <AccessibleSwitch
                                                        checked={notificationSettings[item.key as keyof NotificationSettings]}
                                                        label={item.label}
                                                        onClick={() => handleNotificationChange(item.key as keyof NotificationSettings, !notificationSettings[item.key as keyof NotificationSettings])}
                                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings[item.key as keyof NotificationSettings]
                                                            ? 'bg-blue-600'
                                                            : 'bg-gray-200 dark:bg-gray-600'
                                                            }`}
                                                    >
                                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${notificationSettings[item.key as keyof NotificationSettings] ? 'translate-x-6' : 'translate-x-1'
                                                            }`} />
                                                    </AccessibleSwitch>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Privacy Settings */}
                        {activeTab === 'privacy' && (
                            <div>
                                <h2 className="text-lg sm:text-xl md:text-xl font-semibold text-gray-900 dark:text-white mb-6">
                                    Privacy Settings
                                </h2>

                                <div className="space-y-6">
                                    <div>
                                        <SelectDropdown
                                            value={privacySettings.profileVisibility}
                                            onChange={(value) => handlePrivacyChange('profileVisibility', value)}
                                            options={[
                                                { value: 'public', label: 'Public - Anyone can view your profile' },
                                                { value: 'registered', label: 'Registered Users - Only registered users can view' },
                                                { value: 'private', label: 'Private - Only you can view your profile' }
                                            ]}
                                            label="Profile Visibility"
                                            placeholder="Select visibility option"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        {([
                                            { key: 'showLocation', label: 'Show Location', description: 'Display your city/location on your profile' },
                                            { key: 'showContactInfo', label: 'Show Contact Information', description: 'Allow others to see your contact details' },
                                            { key: 'showLastSeen', label: 'Show Last Seen', description: 'Display when you were last active' }
                                        ] as Array<{ key: 'showLocation' | 'showContactInfo' | 'showLastSeen'; label: string; description: string }>).map(item => (
                                            <div key={item.key} className="flex items-start justify-between">
                                                <div>
                                                    <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 font-medium">{item.label}</span>
                                                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{item.description}</p>
                                                </div>
                                                <AccessibleSwitch
                                                    checked={privacySettings[item.key]}
                                                    label={item.label}
                                                    onClick={() => handlePrivacyChange(item.key, !privacySettings[item.key])}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings[item.key]
                                                        ? 'bg-blue-600'
                                                        : 'bg-gray-200 dark:bg-gray-600'
                                                        }`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${privacySettings[item.key] ? 'translate-x-6' : 'translate-x-1'
                                                        }`} />
                                                </AccessibleSwitch>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Account Security */}
                        {activeTab === 'account' && (
                            <div>
                                <h2 className="text-lg sm:text-xl md:text-xl font-semibold text-gray-900 dark:text-white mb-6">
                                    Account Security
                                </h2>

                                <div className="space-y-8">
                                    {/* Change Password */}
                                    <div className="border-b border-gray-200 dark:border-gray-600 pb-6">
                                        <h3 className="text-base sm:text-lg md:text-lg font-medium text-gray-900 dark:text-white mb-4">
                                            Change Password
                                        </h3>

                                        <form onSubmit={handlePasswordUpdate} className="space-y-4">
                                            <div>
                                                <label htmlFor="settings-current-password" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Current Password
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        id="settings-current-password"
                                                        type={showPasswords.current ? 'text' : 'password'}
                                                        value={passwordForm.currentPassword}
                                                        onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                                                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-xs sm:text-sm md:text-base text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePasswordVisibility('current')}
                                                        aria-label={showPasswords.current ? 'Hide current password' : 'Show current password'}
                                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                    >
                                                        {showPasswords.current ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <label htmlFor="settings-new-password" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    New Password
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        id="settings-new-password"
                                                        type={showPasswords.new ? 'text' : 'password'}
                                                        value={passwordForm.newPassword}
                                                        onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                                                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-xs sm:text-sm md:text-base text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePasswordVisibility('new')}
                                                        aria-label={showPasswords.new ? 'Hide new password' : 'Show new password'}
                                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                    >
                                                        {showPasswords.new ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <label htmlFor="settings-confirm-password" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Confirm New Password
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        id="settings-confirm-password"
                                                        type={showPasswords.confirm ? 'text' : 'password'}
                                                        value={passwordForm.confirmPassword}
                                                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-xs sm:text-sm md:text-base text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePasswordVisibility('confirm')}
                                                        aria-label={showPasswords.confirm ? 'Hide password confirmation' : 'Show password confirmation'}
                                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                    >
                                                        {showPasswords.confirm ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {loading ? 'Updating...' : 'Update Password'}
                                            </button>
                                        </form>
                                    </div>

                                    {/* Security Settings */}
                                    <div>
                                        <h3 className="text-base sm:text-lg md:text-lg font-medium text-gray-900 dark:text-white mb-4">
                                            Security Options
                                        </h3>

                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 font-medium">Two-Factor Authentication</span>
                                                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Add an extra layer of security to your account</p>
                                                </div>
                                                <AccessibleSwitch
                                                    checked={accountSettings.twoFactorEnabled}
                                                    label="two-factor authentication"
                                                    onClick={() => handleAccountChange('twoFactorEnabled', !accountSettings.twoFactorEnabled)}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${accountSettings.twoFactorEnabled
                                                        ? 'bg-blue-600'
                                                        : 'bg-gray-200 dark:bg-gray-600'
                                                        }`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${accountSettings.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                                                        }`} />
                                                </AccessibleSwitch>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 font-medium">Login Alerts</span>
                                                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Get notified of new login attempts</p>
                                                </div>
                                                <AccessibleSwitch
                                                    checked={accountSettings.loginAlerts}
                                                    label="login alerts"
                                                    onClick={() => handleAccountChange('loginAlerts', !accountSettings.loginAlerts)}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${accountSettings.loginAlerts
                                                        ? 'bg-blue-600'
                                                        : 'bg-gray-200 dark:bg-gray-600'
                                                        }`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${accountSettings.loginAlerts ? 'translate-x-6' : 'translate-x-1'
                                                        }`} />
                                                </AccessibleSwitch>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 font-medium">Theme Preference</span>
                                                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Choose between light and dark theme</p>
                                                </div>
                                                <AccessibleSwitch
                                                    checked={isDarkMode}
                                                    label="theme"
                                                    onClick={toggleTheme}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDarkMode
                                                        ? 'bg-blue-600'
                                                        : 'bg-gray-200 dark:bg-gray-600'
                                                        }`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${isDarkMode ? 'translate-x-6' : 'translate-x-1'
                                                        }`} />
                                                    {isDarkMode ? (
                                                        <FiMoon className="absolute left-1.5 w-3 h-3 text-blue-600" />
                                                    ) : (
                                                        <FiSun className="absolute right-1.5 w-3 h-3 text-orange-500" />
                                                    )}
                                                </AccessibleSwitch>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Currency Settings */}
                        {activeTab === 'currency' && (
                            <div>
                                <CurrencySettings />
                            </div>
                        )}

                        {/* Data & Privacy */}
                        {activeTab === 'data' && (
                            <div>
                                <h2 className="text-lg sm:text-xl md:text-xl font-semibold text-gray-900 dark:text-white mb-6">
                                    Data & Privacy
                                </h2>

                                <div className="space-y-8">
                                    <div className="border-b border-gray-200 dark:border-gray-600 pb-6">
                                        <h3 className="text-base sm:text-lg md:text-lg font-medium text-gray-900 dark:text-white mb-4">
                                            Cache Management
                                        </h3>
                                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4">
                                            Control cached data for faster load times or refresh it when needed.
                                        </p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            <button
                                                onClick={() => handleCacheAction('subscription')}
                                                disabled={cacheActionInProgress}
                                                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                <FiRefreshCw className={`w-4 h-4 ${cacheAction === 'subscription' ? 'animate-spin' : ''}`} />
                                                Clear Subscription Cache
                                            </button>
                                            <button
                                                onClick={() => handleCacheAction('access')}
                                                disabled={cacheActionInProgress}
                                                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                <FiRefreshCw className={`w-4 h-4 ${cacheAction === 'access' ? 'animate-spin' : ''}`} />
                                                Clear Access Limits Cache
                                            </button>
                                            <button
                                                onClick={() => handleCacheAction('verification')}
                                                disabled={cacheActionInProgress}
                                                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                <FiRefreshCw className={`w-4 h-4 ${cacheAction === 'verification' ? 'animate-spin' : ''}`} />
                                                Clear Verification Cache
                                            </button>
                                            <button
                                                onClick={() => handleCacheAction('stats')}
                                                disabled={cacheActionInProgress}
                                                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                <FiRefreshCw className={`w-4 h-4 ${cacheAction === 'stats' ? 'animate-spin' : ''}`} />
                                                Clear Analytics Cache
                                            </button>
                                            <button
                                                onClick={() => handleCacheAction('profile')}
                                                disabled={cacheActionInProgress}
                                                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                <FiRefreshCw className={`w-4 h-4 ${cacheAction === 'profile' ? 'animate-spin' : ''}`} />
                                                Clear Profile Views Cache
                                            </button>
                                        </div>
                                        <div className="mt-4 flex flex-wrap gap-3">
                                            <button
                                                onClick={() => handleCacheAction('all')}
                                                disabled={cacheActionInProgress}
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                <FiRefreshCw className={`w-4 h-4 ${cacheAction === 'all' ? 'animate-spin' : ''}`} />
                                                Invalidate All Caches
                                            </button>
                                            <button
                                                onClick={() => handleCacheAction('reset')}
                                                disabled={cacheActionInProgress}
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                <FiTrash2 className={`w-4 h-4 ${cacheAction === 'reset' ? 'animate-spin' : ''}`} />
                                                Reset Cache State
                                            </button>
                                        </div>
                                    </div>
                                    <div className="border-b border-gray-200 dark:border-gray-600 pb-6">
                                        <h3 className="text-base sm:text-lg md:text-lg font-medium text-gray-900 dark:text-white mb-4">
                                            Data Export
                                        </h3>
                                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4">
                                            Download a complete copy of your personal data including profile information, gig applications, message history, reviews, and statistics. Data will be provided as a beautifully formatted PDF document for your records.
                                        </p>
                                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 mb-4">
                                            <div className="flex items-start gap-2">
                                                <FiDownload className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                                <div className="text-xs sm:text-sm">
                                                    <p className="text-blue-800 dark:text-blue-200 font-medium mb-1">Direct Download Available</p>
                                                    <p className="text-blue-700 dark:text-blue-300">
                                                        Your data will be instantly downloaded to your device as a PDF file.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleDataExport}
                                            disabled={loading}
                                            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <FiDownload className="w-4 h-4" />
                                            {loading ? 'Preparing Download...' : 'Download My Data'}
                                        </button>
                                    </div>

                                    <div className="border-b border-gray-200 dark:border-gray-600 pb-6">
                                        <h3 className="text-base sm:text-lg md:text-lg font-medium text-gray-900 dark:text-white mb-4">
                                            Account Deactivation
                                        </h3>
                                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4">
                                            Temporarily deactivate your account. You can reactivate it within 30 days by logging in.
                                        </p>
                                        <button
                                            onClick={() => setShowDeactivateDialog(true)}
                                            disabled={loading}
                                            className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50"
                                        >
                                            Deactivate Account
                                        </button>
                                    </div>

                                    <div>
                                        <h3 className="text-base sm:text-lg md:text-lg font-medium text-red-600 dark:text-red-400 mb-4">
                                            Danger Zone
                                        </h3>
                                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                                            <h4 className="text-xs sm:text-sm font-medium text-red-800 dark:text-red-300 mb-2">
                                                Delete Account
                                            </h4>
                                            <p className="text-red-600 dark:text-red-400 text-xs sm:text-sm mb-4">
                                                Permanently delete your account and all associated data. This action cannot be undone.
                                            </p>
                                            <button
                                                onClick={() => setShowDeleteDialog(true)}
                                                disabled={loading}
                                                className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                                Delete Account
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {loading && (
                            <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 flex items-center justify-center rounded-lg">
                                <LoadingSpinner />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Dialogs */}
            <ConfirmDialog
                isOpen={showDeactivateDialog}
                onClose={() => setShowDeactivateDialog(false)}
                onConfirm={handleAccountDeactivation}
                title="Deactivate Account"
                message="Your account will be temporarily deactivated. You can reactivate it within 30 days by logging in."
                confirmText="Deactivate"
                cancelText="Cancel"
                type="warning"
                loading={loading}
            />

            <DeleteAccountDialog
                isOpen={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleAccountDeletion}
                loading={loading}
            />
        </>
    )
}

export default SettingsPage